import { useEffect, useMemo, useRef, useState } from "react";

import {
  faCheck,
  faMinus,
  faWarning,
  faXmark,
} from "@fortawesome/pro-regular-svg-icons";
import { faArrowTurnDownRight } from "@fortawesome/pro-solid-svg-icons";
import { times } from "lodash-es";

import {
  computeToolCallStatus,
  ToolCallAccordionItem,
  type ToolCallStatus,
  type ToolCallWithResult,
} from "src/base-components/agents/ToolCallDisplay";
import { BetaPill } from "src/base-components/BetaPill";
import { ChatBody } from "src/base-components/Chat/ChatBody";
import { ChatEmptyState } from "src/base-components/Chat/ChatEmptyState";
import { ChatInputArea } from "src/base-components/Chat/ChatInputArea";
import { ChatLoadingIndicator } from "src/base-components/Chat/ChatLoadingIndicator";
import { ChatMessage } from "src/base-components/Chat/ChatMessage";
import { ChatMessageList } from "src/base-components/Chat/ChatMessageList";
import { ChatTextContent } from "src/base-components/Chat/ChatTextContent";
import { ChatToolApproval } from "src/base-components/Chat/ChatToolApproval";
import { ChatUserBubble } from "src/base-components/Chat/ChatUserBubble";
import { ChatEmptyStatePrompt } from "src/base-components/Chat/types";
import { normalizeAnimationSpeed } from "src/base-components/Chat/utils";
import {
  FloatingWindowInternal,
  useFloatingWindowPosition,
} from "src/base-components/FloatingWindow/FloatingWindow";
import { useFloatingWindowsActions } from "src/base-components/FloatingWindow/hooks";
import { FloatingWindowProps } from "src/base-components/FloatingWindow/store";
import { Pill } from "src/base-components/Pill";
import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import {
  CopilotSessionViewer,
  type SessionOverride,
  useSessionOverride,
} from "src/cases/copilot/CopilotSessionViewer";
import { buildCaseCopilotRefMessage } from "src/cases/copilot/caseCopilotFieldRef";
import { parseMessageReference } from "src/cases/copilot/parseMessageReference";
import { useAgentVersionChange } from "src/cases/copilot/useAgentVersionChange";
import type { CopilotChatMessage } from "src/cases/copilot/useCaseCopilotSession";
import {
  getSessionId,
  useCaseCopilotSession,
} from "src/cases/copilot/useCaseCopilotSession";
import {
  useChatScrollAnchor,
  useSetScrollToBottom,
} from "src/cases/copilot/useChatScroll";
import { useCase } from "src/cases/queries/cases";
import { FeatureQueryConversationContextContextTypeEnum } from "src/clients/copilot";
import { CopilotTitle } from "src/copilot/CopilotTitle";
import { PromptBox } from "src/copilot/PromptBox";
import { TitleHelpContent } from "src/copilot/TitleHelpContent";
import { useCopilotPrewarm } from "src/copilot/useCopilotPrewarm";
import { Callout } from "src/design-system/Callout";
import { Icon, IconButton } from "src/design-system/Icon";
import { usePowertools } from "src/powertools/hooks";
import { CasePageParams } from "src/router/urls";
import { useCurrentUserId } from "src/store/AuthStore";
import { useParamsDecode } from "src/utils/useParamsDecode";

const SUGGESTION_PROMPTS: ChatEmptyStatePrompt[] = [
  {
    label: "What is the source of the funds in question?",
    prompt: "What is the source of the funds in question?",
  },
  {
    label: "Any unusual transactions recently?",
    prompt: "Have there been any unusual transactions recently?",
  },
  {
    label: "Any connections to known criminal entitites?",
    prompt: "Are there any connections to known criminal entitites?",
  },
];

const MessageSkeleton: React.FC = () => (
  <li className="flex flex-col gap-y-2">
    <SkeletonPlaceholder height="h-3" width="w-14" />
    <SkeletonPlaceholder height="h-4" width="w-full" />
    <SkeletonPlaceholder height="h-4" width="w-full" />
    <SkeletonPlaceholder height="h-4" width="w-3/4" />
  </li>
);

const ReferenceChip: React.FC<{ label: string }> = ({ label }) => (
  <div className="flex w-fit items-center gap-x-0.5">
    <Icon icon={faArrowTurnDownRight} size="2xs" />
    <Pill size="sm" variant="gray">
      <Pill.Text>{label}</Pill.Text>
    </Pill>
  </div>
);

export const ToolCallCompactRow: React.FC<{
  toolCall: ToolCallWithResult["toolCall"];
  status: ToolCallStatus | null;
}> = ({ toolCall, status }) => (
  <div className="flex items-center justify-between rounded-lg border border-gray-200 px-2 py-1.5">
    <span className="min-w-0 truncate font-inter-medium-12px text-gray-800">
      {toolCall.metadata.display_name}
    </span>
    {status && (
      <Pill size="sm" variant={status === "error" ? "red" : "green"}>
        <Pill.Icon icon={status === "error" ? faWarning : faCheck} />
        <Pill.Text>{status === "error" ? "Failed" : "Successful"}</Pill.Text>
      </Pill>
    )}
  </div>
);

export const ToolCallGroupMessage: React.FC<{
  toolCallsWithResults: ToolCallWithResult[];
}> = ({ toolCallsWithResults }) => {
  const [expanded, setExpanded] = useState(false);
  const isPowerToolsEnabled = usePowertools();
  const count = toolCallsWithResults.length;

  return (
    <li>
      <button
        className="font-inter-normal-12px text-gray-400"
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
      >
        Called {count} {count === 1 ? "tool" : "tools"}
      </button>
      {expanded && (
        <div className="mt-1 space-y-2">
          {toolCallsWithResults.map((tcwr) =>
            isPowerToolsEnabled ? (
              <ToolCallAccordionItem
                key={tcwr.toolCall.id}
                status={computeToolCallStatus(tcwr.toolResult, null)}
                toolCall={tcwr.toolCall}
                toolResult={tcwr.toolResult}
              />
            ) : (
              <ToolCallCompactRow
                key={tcwr.toolCall.id}
                status={computeToolCallStatus(tcwr.toolResult, null)}
                toolCall={tcwr.toolCall}
              />
            ),
          )}
        </div>
      )}
    </li>
  );
};

/** Progressively reveals text via requestAnimationFrame. */
const useTypewriterText = (content: string, enabled: boolean): string => {
  const [charCount, setCharCount] = useState(enabled ? 0 : content.length);
  const contentRef = useRef(content);
  const enabledRef = useRef(enabled);

  if (enabled && (!enabledRef.current || content !== contentRef.current)) {
    setCharCount(0);
  }
  contentRef.current = content;
  enabledRef.current = enabled;

  useEffect(() => {
    if (!enabled) {
      setCharCount(content.length);
      return;
    }

    const duration = normalizeAnimationSpeed(content.length) * 1000;
    const start = performance.now();

    let rafId: number;
    const tick = () => {
      const elapsed = performance.now() - start;
      const progress = Math.min(elapsed / duration, 1);
      setCharCount(Math.round(progress * content.length));
      if (progress < 1) rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [content, enabled]);

  const sliced = content.slice(0, charCount);
  // If mid-table-row, extend to the end of the line to avoid column shifting.
  const lastNl = sliced.lastIndexOf("\n");
  if (sliced.slice(lastNl + 1).startsWith("|")) {
    const rowEnd = content.indexOf("\n", charCount);
    return content.slice(0, rowEnd === -1 ? content.length : rowEnd);
  }
  return sliced;
};

const CopilotMessage: React.FC<{
  message: CopilotChatMessage;
  animate?: boolean;
}> = ({ message, animate = false }) => {
  const rawContent = message.role !== "tool-group" ? message.content : "";
  const displayContent = useTypewriterText(
    rawContent,
    animate && message.role === "assistant",
  );

  if (message.role === "tool-group") {
    return (
      <ToolCallGroupMessage
        toolCallsWithResults={message.toolCallsWithResults}
      />
    );
  }

  if (message.role === "user") {
    const { ref, text } = parseMessageReference(message.content);
    return (
      <ChatMessage role="user">
        {ref && (
          <div className="mb-1 flex justify-end">
            <ReferenceChip label={ref} />
          </div>
        )}
        <ChatUserBubble message={text} />
      </ChatMessage>
    );
  }

  return (
    <ChatMessage role={message.role}>
      <ChatTextContent content={displayContent} delay={0} useMarkdownRenderer />
    </ChatMessage>
  );
};

export const CaseCopilotWindow: React.FC<FloatingWindowProps> = ({
  computePosition,
  id,
  data: windowData,
}) => {
  const { position, windowRef } = useFloatingWindowPosition(computePosition);
  const { close: closeWindow, updateData } = useFloatingWindowsActions();
  const { case_id: caseId } = useParamsDecode<CasePageParams>();
  const userId = useCurrentUserId();
  const { data: caseData } = useCase(caseId);
  const isPowerToolsEnabled = usePowertools();

  const copilotReference = caseData?.data.template_version?.copilot_reference;
  const agentId = copilotReference?.agent_id;
  const agentVersionId = copilotReference?.latest_agent_version_id;
  const environment = caseData?.data.env ?? "sandbox";

  const fileIds =
    caseData?.data.file_categories?.flatMap((cat) => cat.file_ids ?? []) ?? [];

  const {
    activeVersionId,
    hasVersionChanged,
    acceptNewVersion,
    dismissChange,
  } = useAgentVersionChange({
    caseId,
    latestVersionId: agentVersionId,
  });

  const {
    viewingOverride,
    setViewingOverride,
    effectiveUserId,
    effectiveAgentVersionId,
    isViewingOtherSession,
  } = useSessionOverride({ currentUserId: userId, activeVersionId });
  useCopilotPrewarm({
    agentId,
    sessionId: getSessionId(userId, caseId, activeVersionId),
    enabled: true,
  });

  const refLabel = windowData?.refLabel as string | undefined;
  const formKind = windowData?.formKind as "task" | "action" | undefined;
  const formKey = windowData?.formKey as string | undefined;
  const fieldKey = windowData?.fieldKey as string | undefined;

  const activeFieldRef =
    refLabel != null &&
    (formKind === "task" || formKind === "action") &&
    formKey != null &&
    formKey.length > 0 &&
    fieldKey != null &&
    fieldKey.length > 0
      ? { label: refLabel, formKind, formKey, fieldKey }
      : null;

  const refChipLabel = activeFieldRef?.label;
  const clearFieldRef = () => {
    updateData(id, {});
  };

  const {
    messages,
    isLoading,
    isActive,
    failureMessage,
    sendMessage,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useCaseCopilotSession({
    agentId,
    agentVersionId: effectiveAgentVersionId,
    caseId,
    userId: effectiveUserId,
    environment,
    fileIds,
  });

  // Silently upgrade to the latest agent version when conversation is empty
  useEffect(() => {
    if (
      hasVersionChanged &&
      !isLoading &&
      !isViewingOtherSession &&
      messages.length === 0
    ) {
      acceptNewVersion();
    }
  }, [
    hasVersionChanged,
    isLoading,
    isViewingOtherSession,
    messages.length,
    acceptNewVersion,
  ]);

  const chronologicalMessages = useMemo(
    () => [...messages].reverse(),
    [messages],
  );

  // Non-zero = typewriter animation active. Incremented on each send to re-trigger the rAF effect.
  const sendEpochRef = useRef(0);
  const [animationEpoch, setAnimationEpoch] = useState(0);
  const isAnimating = animationEpoch > 0;

  const lastUserIndex = useMemo(() => {
    if (!isAnimating) return -1;
    return chronologicalMessages.findLastIndex((m) => m.role === "user");
  }, [isAnimating, chronologicalMessages]);

  const shouldAnimate = (msg: CopilotChatMessage, index: number) =>
    isAnimating &&
    msg.role === "assistant" &&
    msg.id != null &&
    index > lastUserIndex;

  const listRef = useRef<HTMLUListElement>(null);

  const { resetScrollToBottom } = useSetScrollToBottom({
    listRef,
    messages,
    isLoading,
  });

  const { activateAnchor, clearAnchor } = useChatScrollAnchor({
    listRef,
    messages: chronologicalMessages,
    isActive,
    failureMessage,
    isFetchingNextPage,
  });

  // Stable ref so the wheel handler can clear the anchor without
  // restarting the effect on every render.
  const clearAnchorRef = useRef(clearAnchor);
  clearAnchorRef.current = clearAnchor;

  // Survives effect re-runs so a wheel-up stays honoured across isActive toggles.
  const pinnedRef = useRef(true);

  // Keep the list pinned to the bottom while the typewriter reveals content.
  // A wheel-up gesture unpins so the user can scroll freely and load older pages.
  useEffect(() => {
    if (animationEpoch === 0) return;
    const list = listRef.current;
    if (!list) return;

    let rafId: number;
    const deadline = isActive ? Infinity : performance.now() + 4_000;

    const onWheel = (e: WheelEvent) => {
      if (e.deltaY < 0) {
        pinnedRef.current = false;
        clearAnchorRef.current();
      }
    };
    list.addEventListener("wheel", onWheel);

    const scroll = () => {
      if (performance.now() > deadline) {
        setAnimationEpoch(0);
        return;
      }
      if (pinnedRef.current) {
        list.scrollTop = list.scrollHeight;
      }
      rafId = requestAnimationFrame(scroll);
    };
    rafId = requestAnimationFrame(scroll);

    return () => {
      cancelAnimationFrame(rafId);
      list.removeEventListener("wheel", onWheel);
    };
  }, [animationEpoch, isActive]);

  const [prompt, setPrompt] = useState("");

  const handleSessionOverride = (override: SessionOverride | null) => {
    setViewingOverride(override);
    setAnimationEpoch(0);
    clearAnchor();
    resetScrollToBottom();
  };

  const handleSend = (text: string) => {
    if (isViewingOtherSession) return;
    const trimmed = text.trim();
    if (!trimmed) return;

    const message = activeFieldRef
      ? buildCaseCopilotRefMessage(activeFieldRef, trimmed)
      : trimmed;
    activateAnchor();
    pinnedRef.current = true;
    sendEpochRef.current += 1;
    setAnimationEpoch(sendEpochRef.current);
    sendMessage(message);
    setPrompt("");
    clearFieldRef();
  };

  const handleReset = () => {
    setPrompt("");
    setAnimationEpoch(0);
    clearAnchor();
    clearFieldRef();
  };

  const handleAcceptNewVersion = () => {
    acceptNewVersion();
    handleReset();
  };

  return (
    <FloatingWindowInternal
      ref={windowRef}
      clipOverflow={false}
      closeIcon={faMinus}
      id={id}
      height="h-150"
      helpText={
        <TitleHelpContent description="Ask questions about this case and get help with analysis, actions, and next steps." />
      }
      isPinned={false}
      maximizable={false}
      style={{
        inset: "0px auto auto 0px",
        transform: position,
      }}
      title={
        <span className="flex items-center gap-2">
          <CopilotTitle /> <BetaPill />
        </span>
      }
      titleRight={
        isPowerToolsEnabled ? (
          <CopilotSessionViewer
            currentAgentVersionId={activeVersionId}
            viewingOverride={viewingOverride}
            onSubmit={handleSessionOverride}
          />
        ) : undefined
      }
      draggable
      resizeable
      onClose={() => closeWindow(id)}
    >
      <ChatBody>
        {messages.length === 0 && !isLoading ? (
          isViewingOtherSession ? (
            <div className="flex h-full flex-grow items-center justify-center p-2 font-inter-normal-12px text-gray-500">
              No messages in this session
            </div>
          ) : (
            <ChatEmptyState
              prompts={SUGGESTION_PROMPTS}
              onPromptClick={handleSend}
            />
          )
        ) : (
          <ChatMessageList
            ref={listRef}
            pagination={{
              hasNextPage,
              isFetchingNextPage,
              fetchNextPage,
              loadingSkeleton: <MessageSkeleton />,
            }}
          >
            {messages.length === 0 && isLoading
              ? times(3, (i) => <MessageSkeleton key={i} />)
              : chronologicalMessages.map((message, index) => (
                  <CopilotMessage
                    key={message.id}
                    animate={shouldAnimate(message, index)}
                    message={message}
                  />
                ))}
            {failureMessage && (
              <li className="mt-4 flex-shrink-0">
                <Callout type="error">{failureMessage}</Callout>
              </li>
            )}
            {hasVersionChanged && !isViewingOtherSession && (
              <li className="rounded-lg border border-gray-200 p-3">
                <ChatToolApproval
                  approveLabel="Start new session"
                  dataLoc="agent-version-change-banner"
                  denyLabel="Continue current session"
                  description="The agent has been updated. Would you like to start a new session with the latest version?"
                  onApprove={handleAcceptNewVersion}
                  onDeny={dismissChange}
                />
              </li>
            )}
            {isActive && (
              <ChatLoadingIndicator
                mode="loading"
                dataLoc="copilot-loading-indicator"
              />
            )}
          </ChatMessageList>
        )}
        <ChatInputArea>
          {isViewingOtherSession ? (
            <div className="flex items-center justify-center py-3 font-inter-normal-12px text-gray-500">
              Viewing another user's session
            </div>
          ) : (
            <PromptBox
              disabled={isActive || prompt.trim().length === 0}
              errored={false}
              mentionTextPlaceholder=""
              minified={messages.length > 0}
              resource={[]}
              resourceType={
                FeatureQueryConversationContextContextTypeEnum.FEATURE_QUERY
              }
              topContent={
                refChipLabel && (
                  <div className="flex items-center justify-between">
                    <ReferenceChip label={refChipLabel} />
                    <IconButton
                      icon={faXmark}
                      size="2xs"
                      onClick={clearFieldRef}
                    />
                  </div>
                )
              }
              value={prompt}
              onChange={setPrompt}
              onReset={handleReset}
              onSubmit={handleSend}
            />
          )}
        </ChatInputArea>
      </ChatBody>
    </FloatingWindowInternal>
  );
};
